import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getAllDeliveryChallans } from '../../services/DeliveryChallanServices';
import { buildListExcelExportHeader } from '../../utils/listExcelExportHeader';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import {
  TODAY_QTY_DETAIL_COLUMNS,
  TODAY_QTY_MODE_SUMMARY_COLUMNS,
  mapDeliveryChallanForTodayQty,
  buildTodayQtyDetailRows,
  buildTodayQtyModeSummaryRows,
  formatTodayQtyDetailDisplayRows,
  formatTodayQtyModeSummaryDisplayRows,
  buildTodayQtySummaryTotals,
  formatQty,
} from './todayQtySummaryUtils';

function TodayQtySummaryModal({
  open,
  onClose,
  cookies,
  plantId,
  period = 'day',
}) {
  const isMonth = period === 'month';
  const documentTitle = isMonth ? 'Month Qty Summary' : "Today's Qty Summary";
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [rows, setRows] = useState([]);
  const [companyHeader, setCompanyHeader] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [activeTab, setActiveTab] = useState('detailed');

  const dateLabel = useMemo(() => {
    if (!isMonth) {
      return selectedDate.format('DD-MM-YYYY');
    }
    const from = selectedDate.clone().startOf('month').format('DD-MM-YYYY');
    const upto = selectedDate.clone().endOf('month').format('DD-MM-YYYY');
    return `${from} to ${upto}`;
  }, [isMonth, selectedDate]);

  const plantIds = useMemo(
    () => (plantId ? [plantId] : []),
    [plantId],
  );

  const loadCompanyHeader = useCallback(async () => {
    try {
      const exportHeader = await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle,
      });
      setCompanyHeader(exportHeader);
    } catch (error) {
      setCompanyHeader(null);
    }
  }, [cookies, plantIds, documentTitle]);

  const loadRowsForDate = useCallback(async (dateMoment) => {
    setLoading(true);
    const from = isMonth ? dateMoment.clone().startOf('month') : dateMoment.clone();
    const upto = isMonth ? dateMoment.clone().endOf('month') : dateMoment.clone();
    const params = {
      from_date: from.format('DD-MM-YYYY'),
      upto_date: upto.format('DD-MM-YYYY'),
    };
    if (plantId) {
      params.plant_ids = String(plantId);
    }
    try {
      const response = await getAllDeliveryChallans(cookies, params);
      const mapped = (response?.delivery_challan_list || []).map(mapDeliveryChallanForTodayQty);
      setRows(mapped);
    } catch (error) {
      displayError(error?.response?.data ?? error?.message, 'Failed to load delivery challans');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cookies, plantId, isMonth]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const anchor = isMonth ? moment().startOf('month') : moment();
    setSelectedDate(anchor);
    setActiveTab('detailed');
    loadCompanyHeader();
    loadRowsForDate(anchor);
  }, [open, isMonth, loadRowsForDate, loadCompanyHeader]);

  const detailRows = useMemo(() => buildTodayQtyDetailRows(rows), [rows]);
  const modeSummaryRows = useMemo(() => buildTodayQtyModeSummaryRows(rows), [rows]);
  const isSummaryTab = activeTab === 'summary';
  const activeColumns = isSummaryTab ? TODAY_QTY_MODE_SUMMARY_COLUMNS : TODAY_QTY_DETAIL_COLUMNS;
  const displayRows = useMemo(
    () => (isSummaryTab
      ? formatTodayQtyModeSummaryDisplayRows(modeSummaryRows)
      : formatTodayQtyDetailDisplayRows(detailRows)),
    [isSummaryTab, modeSummaryRows, detailRows],
  );
  const totals = useMemo(
    () => buildTodayQtySummaryTotals(isSummaryTab ? modeSummaryRows : detailRows),
    [isSummaryTab, modeSummaryRows, detailRows],
  );
  const canExport = !loading && displayRows.length > 0;
  const reportTitle = `${documentTitle} (${dateLabel})`;

  const shiftDate = (step) => {
    const nextDate = isMonth
      ? selectedDate.clone().add(step, 'months').startOf('month')
      : selectedDate.clone().add(step, 'days');
    setSelectedDate(nextDate);
    loadRowsForDate(nextDate);
  };

  const goToday = () => {
    const anchor = isMonth ? moment().startOf('month') : moment();
    setSelectedDate(anchor);
    loadRowsForDate(anchor);
  };

  const handleExcelExport = async () => {
    setExportingExcel(true);
    try {
      const columns = activeColumns.map((column) => column.label);
      const totalCells = activeColumns.map((column, index) => {
        if (index === 0) {
          return 'Total';
        }
        if (column.key === 'dcQty') {
          return formatQty(totals.dcQty);
        }
        if (column.key === 'acceptedQty') {
          return formatQty(totals.acceptedQty);
        }
        if (column.key === 'loadCount') {
          return totals.loadCount;
        }
        return '';
      });
      const rows = [
        ...displayRows.map((row) => activeColumns.map((column) => row[column.key])),
        totalCells,
      ];
      const fileStamp = dateLabel.replace(/\s+/g, '_');
      exportAsExcel(columns, rows, `${isMonth ? 'month' : 'today'}_qty_${activeTab}_${fileStamp}.xlsx`);
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePdfExport = async () => {
    setExportingPdf(true);
    try {
      const exportHeader = companyHeader || await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle,
        extraInfoLines: [`${isMonth ? 'Period' : 'Date'}: ${dateLabel}`],
      });
      const pdfColumns = activeColumns.map((column) => ({
        header: column.label,
        dataKey: column.key,
        align: column.align === 'right' ? 'right' : 'left',
      }));
      const totalRow = { [activeColumns[0].key]: 'Total' };
      activeColumns.slice(1).forEach((column) => {
        if (column.key === 'dcQty') {
          totalRow[column.key] = formatQty(totals.dcQty);
        } else if (column.key === 'acceptedQty') {
          totalRow[column.key] = formatQty(totals.acceptedQty);
        } else if (column.key === 'loadCount') {
          totalRow[column.key] = totals.loadCount;
        } else {
          totalRow[column.key] = '';
        }
      });
      const pdfRows = [...displayRows, totalRow];
      const blob = createPdfBlob(
        '',
        pdfColumns,
        pdfRows,
        exportHeader.infoLines?.length
          ? exportHeader.infoLines
          : [`${isMonth ? 'Period' : 'Date'}: ${dateLabel}`],
        {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
          documentTitle,
          subtitle: dateLabel,
        },
      );
      openPdfInApp(blob, {
        title: reportTitle,
        downloadFileName: `${isMonth ? 'month' : 'today'}_qty_${activeTab}_${dateLabel.replace(/\s+/g, '_')}.pdf`,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="modal today-qty-summary-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header today-qty-summary-modal-header">
            <h5 className="modal-title">{reportTitle}</h5>
            <div className="today-qty-summary-header-actions">
              {canExport && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="View PDF"
                    aria-label="View PDF"
                    disabled={exportingPdf}
                    onClick={handlePdfExport}
                  >
                    <i className="fa fa-file-pdf-o text-danger" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="Download Excel"
                    aria-label="Download Excel"
                    disabled={exportingExcel}
                    onClick={handleExcelExport}
                  >
                    <i className="fa fa-file-excel-o text-success" aria-hidden="true" />
                  </button>
                </>
              )}
              <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body">
            <div className="today-qty-summary-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                className={!isSummaryTab ? 'is-active' : undefined}
                aria-selected={!isSummaryTab}
                onClick={() => setActiveTab('detailed')}
              >
                Detailed
              </button>
              <button
                type="button"
                role="tab"
                className={isSummaryTab ? 'is-active' : undefined}
                aria-selected={isSummaryTab}
                onClick={() => setActiveTab('summary')}
              >
                Summary
              </button>
            </div>
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : displayRows.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No delivery challans found for {dateLabel}.
              </div>
            ) : (
              <div className="table-responsive no-column-freeze">
                <table className="table table-bordered today-qty-summary-table">
                  <thead>
                    <tr>
                      {activeColumns.map((column) => (
                        <th
                          key={column.key}
                          className={column.align === 'right' ? 'text-right' : undefined}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, index) => (
                      <tr key={`${row.date || ''}-${row.customer}-${row.site}-${row.grade}-${row.deliveryMode || row.marketing}-${index}`}>
                        {activeColumns.map((column) => (
                          <td
                            key={column.key}
                            className={column.align === 'right' ? 'text-right' : undefined}
                          >
                            {row[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="today-qty-summary-total-row">
                      {activeColumns.map((column, index) => {
                        let value = '';
                        if (index === 0) {
                          value = 'Total';
                        } else if (column.key === 'dcQty') {
                          value = formatQty(totals.dcQty);
                        } else if (column.key === 'acceptedQty') {
                          value = formatQty(totals.acceptedQty);
                        } else if (column.key === 'loadCount') {
                          value = totals.loadCount;
                        }
                        return (
                          <td
                            key={column.key}
                            className={column.align === 'right' ? 'text-right' : undefined}
                          >
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer today-qty-summary-modal-footer">
            <div className="today-qty-summary-nav">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(-1)}
                disabled={loading}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={goToday}
                disabled={loading}
              >
                {isMonth ? 'This Month' : 'Today'}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(1)}
                disabled={loading}
              >
                Next
              </button>
            </div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodayQtySummaryModal;
